import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { url } from '@ux/header-util';
import User from '@ux/icon/user';
import Link from '@ux/link';
import { TrayMenu } from '@ux/tray-menu';
import DropdownHeader from './dropdown-header';
import { golf } from './enums';
import golfToEid from 'golf-to-eid';
import Links from './links';
import Pro from './pro';
import UserDetails from './user-details';

/**
 * Render user dropdown menu.
 *
 * @Constructor
 * @function AccountTray
 * @api public
 * @param {props} props passed properties to function
 */
export function AccountTray(props) {
  const { loggedIn } = props;
  const [isOpen, setIsOpen] = useState(false);
  const status = loggedIn ? 'signout' : 'signin';
  const privateLabelId = props.privateLabelId;
  //
  // Generate Traffic2 eids to be used to track clicks. Map the generated
  // strings back to the same keys the Golf translations strings have.
  //

  const eids = useMemo(() => {
    return Object.entries(golf).reduce((acc, [key, value]) => {
      acc[key] = golfToEid(value, {
        prefix: `${props.eidPrefix}_account_tray`,
        postfix: 'link.click'
      });
      return acc;
    }, {});
  }, [props.eidPrefix]);

  let trayName = <User />;

  if (privateLabelId === 1) {
    if (loggedIn) {
      // props.displayName is the short name (e.g. "Jane").
      trayName = (
        <React.Fragment>
          <span className='desktop-account-tray-name'>
            { props.displayName }
          </span>
          <span className='mobile-account-tray-name'>
            <User />
          </span>
        </React.Fragment>
      );
    } else {
      trayName = (
        <React.Fragment>
          <span className='desktop-account-tray-name'>
            <FormattedMessage id={ golf.signin } />
          </span>
          <span className='mobile-account-tray-name'>
            <User />
          </span>
        </React.Fragment>
      );
    }
  }

  return (
    <div>
      <FormattedMessage
        id={
          props.preset === 'internal-header'
            ? golf.internalModeTitle
            : golf.title
        }>
        { userTrayTitle => (
          <div className='tray-container'>
            <TrayMenu
              ariaLabel={ userTrayTitle }
              trayAlignment='right'
              name={ trayName }
              className='customer-menu'
              showOpenCaret={ true }
              traySpanClass='tray-toggle-span'
              type='base'
              fullwidth={ false }
              onToggle={ setIsOpen.bind(!isOpen) }
              data-eid={ `${props.eidPrefix}_account_tray.button.click` }>
              <div className='customer-info-wrap'>
                <div className='customer-name'>
                  <DropdownHeader
                    loggedIn={ loggedIn }
                    name={ props.name }
                    preset={ props.preset }
                  />
                </div>
                <div className='customer-details'>
                  <UserDetails
                    loggedIn={ loggedIn }
                    urls={ props.urls }
                    name={ props.name }
                    shopperId={ props.shopperId }
                    vip={ props.privateLabelId === 1 && props.vip }
                    username={ props.username }
                    proshopper={ props.proshopper }
                    rewardPoints={ props.rewardPoints }
                    eids={ eids }
                    pcx={ props.pcx }
                  />
                </div>
              </div>
              <div className='tray-heading'>
                <FormattedMessage id={ golf.account } />
              </div>
              <Pro
                loggedIn={ loggedIn }
                proshopper={ props.proshopper }
                eids={ eids }
                urls={ props.urls }
                pcx={ props.pcx }
              />
              <Links
                privateLabelType={ props.privateLabelType }
                privateLabelId={ props.privateLabelId }
                resellerUrls={ props.resellerUrls }
                urls={ props.urls }
                urlArgs={ props.urlArgs }
                bottomLinks={ props.bottomLinks }
                eids={ eids }
              />
              <div className='tray-menu-divider' />
              <Link
                className='customer-link login'
                href={ url.updateQuery(
                  props.urls.sso[status === 'signin' ? 'login' : 'logout'].href,
                  props.sso
                ) }
                data-eid={ eids[status] }>
                <FormattedMessage id={ golf[status] } />
              </Link>
            </TrayMenu>
          </div>
        )}
      </FormattedMessage>
      { isOpen && <div className='account-tray-menu-bg' /> }
    </div>
  );
}

AccountTray.propTypes = {
  bottomLinks: PropTypes.array,
  displayName: PropTypes.string,
  eidPrefix: PropTypes.string,
  loggedIn: PropTypes.bool,
  name: PropTypes.string,
  preset: PropTypes.string,
  privateLabelId: PropTypes.number,
  privateLabelType: PropTypes.number,
  proshopper: PropTypes.bool,
  resellerUrls: PropTypes.object,
  rewardPoints: PropTypes.number,
  shopperId: PropTypes.string,
  sso: PropTypes.object,
  username: PropTypes.string,
  urls: PropTypes.object.isRequired,
  urlArgs: PropTypes.object,
  vip: PropTypes.object,
  pcx: PropTypes.bool
};

AccountTray.defaultProps = {
  bottomLinks: []
};

export default AccountTray;
